// ============================================================================
//  Financial statement builders — Profit & Loss and Balance Sheet.
//
//  P&L is driven by the LEDGER: every voucher posts to the ledger accounts
//  linked at master creation (item Basic Ledger, tax SGST/CGST/IGST ledgers,
//  TDS section ledger, party Linked Ledger), and the P&L simply groups those
//  postings by account, keeping the Income and Expense accounts.
//
//  The Balance Sheet reads the same ledger world where the double-entry is
//  complete, and uses the authoritative source where it isn't (receipts/
//  payments track party outstanding via `paidAmount`, not a party ledger leg):
//    • Cash / Bank / TDS ...... account-master balances (by ledger name)
//    • Sundry Debtors/Creditors  outstanding documents
//    • Closing Stock .......... inventory valuation
//    • GST payable / input ..... output − input on the ledger tax accounts
//    • Capital & other ledgers . their live balances
//  A single "Difference in Opening Balances" plug keeps the sheet transparent
//  when user-entered opening balances don't self-tally.
// ============================================================================

const num = (v) => Number(v) || 0;
const inPeriod = (d, start, end) => (!start || (d && d >= start)) && (!end || (d && d <= end));
const upto = (d, end) => !end || (d && d <= end);

// Taxable (net-of-GST) value of an item document, from its line items.
export function docTaxable(doc) {
  if (Array.isArray(doc.items) && doc.items.length) {
    return doc.items.reduce((s, li) => {
      const t = li.taxableAmount != null ? num(li.taxableAmount) : num(li.qty) * num(li.rate);
      return s + t;
    }, 0);
  }
  // Fallback: strip an approximate tax component if only a grand total exists.
  return num(doc.taxableValue != null ? doc.taxableValue : doc.grandTotal);
}

// Net GST sitting in the books (output collected − input credit). Positive is a
// liability (payable); negative is an asset (input credit receivable).
function netGst(state, end) {
  return (state.ledgerEntries || [])
    .filter(e => upto(e.date, end) && /gst/i.test(e.account || ''))
    .reduce((s, e) => {
      const isOutput = /output/i.test(e.account);
      const isInput = /input/i.test(e.account);
      if (isOutput) return s + num(e.credit) - num(e.debit); // output = credit (liability)
      if (isInput) return s - (num(e.debit) - num(e.credit)); // input = debit (reduces liability)
      return s;
    }, 0);
}

// Nature (Asset/Liability/Income/Expense/Equity) of a named ledger account,
// via the linked account's group when it exists, else a name heuristic.
function classify(state, account) {
  const acc = (state.accounts || []).find(a => a.name === account);
  if (acc) {
    const grp = (state.accountGroups || []).find(g => g.id === acc.groupId);
    const gn = grp?.groupName;
    if (gn) return gn; // 'Asset' | 'Liability' | 'Income' | 'Expense' | 'Equity'
    if (acc.type === 'Cash' || acc.type === 'Bank') return 'Asset';
  }
  const n = (account || '').toLowerCase();
  if (/output/.test(n)) return 'Liability';
  if (/input/.test(n)) return 'Asset';
  if (/capital|equity|reserve|drawings|surplus/.test(n)) return 'Equity';
  if (/creditor|payable|loan|duties|provision/.test(n)) return 'Liability';
  if (/debtor|receivable|advance/.test(n)) return 'Asset';
  if (/cash|bank/.test(n)) return 'Asset';
  if (/sales|income|revenue|discount received|interest received|commission received/.test(n)) return 'Income';
  if (/purchase|expense|cost|wages|salar|rent|freight|charges|round off|discount allowed/.test(n)) return 'Expense';
  return null;
}

// Which side of the P&L a ledger account posts to, decided from its group
// nature (the master linkage) with safe fallbacks. Balance-sheet accounts
// (assets/liabilities/equity — party ledgers, GST, cash/bank, TDS) return null.
function plSide(state, account, type) {
  if (type === 'Expense') return 'indirect';            // an Expense Entry head is always an indirect expense
  const nat = classify(state, account);
  if (nat === 'Asset' || nat === 'Liability' || nat === 'Equity') return null;
  if (nat === 'Income') return 'income';
  if (nat === 'Expense') return /purchase/i.test(account) ? 'direct' : 'indirect';
  // Not in the chart of accounts and no heuristic matched → last-resort by name.
  const n = (account || '').toLowerCase();
  if (/purchase/.test(n)) return 'direct';
  if (/^sales/.test(n)) return 'income';
  return null;
}

// ---------------------------------------------------------------------------
//  Profit & Loss for a period — built straight from the LEDGER postings.
//  Every sale, purchase, expense, journal and direct voucher posts to the
//  ledger set at master creation (item Basic Ledger, tax ledgers, TDS ledger,
//  party Linked Ledger, …). We keep only the Income and Expense accounts —
//  GST, party, cash/bank and TDS legs are balance-sheet items — and present
//  them by their actual ledger names:
//    Gross Profit = Income − Direct (Purchases)
//    Net Profit   = Gross Profit − Indirect Expenses
// ---------------------------------------------------------------------------
export function computeProfitAndLoss(state, start, end) {
  const income = {}, direct = {}, indirect = {};
  (state.ledgerEntries || []).forEach(e => {
    if (!inPeriod(e.date, start, end)) return;
    const side = plSide(state, e.account, e.type);
    if (!side) return;
    const acct = e.account || 'Unnamed';
    if (side === 'income') income[acct] = (income[acct] || 0) + (num(e.credit) - num(e.debit));   // credit-positive
    else if (side === 'direct') direct[acct] = (direct[acct] || 0) + (num(e.debit) - num(e.credit)); // debit-positive
    else indirect[acct] = (indirect[acct] || 0) + (num(e.debit) - num(e.credit));
  });
  const rowsOf = (m) => Object.entries(m)
    .map(([label, amount]) => ({ label, amount: +Number(amount).toFixed(2) }))
    .filter(r => Math.abs(r.amount) >= 0.005)
    .sort((a, b) => b.amount - a.amount);

  const incomeRows = rowsOf(income);
  const directRows = rowsOf(direct);
  const indirectRows = rowsOf(indirect);
  const totalIncome = incomeRows.reduce((s, r) => s + r.amount, 0);
  const totalDirect = directRows.reduce((s, r) => s + r.amount, 0);
  const totalIndirect = indirectRows.reduce((s, r) => s + r.amount, 0);
  const grossProfit = totalIncome - totalDirect;
  const netProfit = grossProfit - totalIndirect;

  return { incomeRows, totalIncome, directRows, totalDirect, grossProfit, indirectRows, totalIndirect, netProfit };
}

// Cumulative net profit from inception up to a date — used as retained earnings
// on the Balance Sheet's equity side.
function cumulativeProfit(state, end) {
  return computeProfitAndLoss(state, null, end).netProfit;
}

// ---------------------------------------------------------------------------
//  Balance Sheet as on a date.
// ---------------------------------------------------------------------------
export function computeBalanceSheet(state, end) {
  const accounts = state.accounts || [];

  // --- Assets ---
  // Each Cash / Bank ledger shown by its own name (from Account Master).
  const cashBankRows = accounts
    .filter(a => a.type === 'Cash' || a.type === 'Bank')
    .map(a => ({ label: a.name, amount: num(a.balance) }))
    .filter(r => Math.abs(r.amount) >= 0.005);

  const debtors = (state.salesInvoices || [])
    .filter(d => upto(d.date, end))
    .reduce((s, d) => s + Math.max(0, num(d.grandTotal) - num(d.paidAmount)), 0);

  const closingStock = (state.items || [])
    .reduce((s, it) => s + Math.max(0, num(it.currentStock)) * num(it.purchaseRate), 0);

  const tdsRecv = num(accounts.find(a => a.name === 'TDS Receivable')?.balance);

  const gst = netGst(state, end); // + liability, − asset
  const gstAsset = gst < 0 ? -gst : 0;
  const gstLiab = gst > 0 ? gst : 0;

  // Other master accounts classified Asset — uses the live balance so direct
  // (non-party) vouchers reflect. Excludes cash/bank and anything already
  // counted from documents (debtors / TDS / GST / stock).
  const excludeAsset = /debtor|receivable|tds|gst|input|stock|inventory/i;
  const otherAssets = accounts
    .filter(a => a.type !== 'Cash' && a.type !== 'Bank' && !excludeAsset.test(a.name))
    .filter(a => classify(state, a.name) === 'Asset')
    .map(a => ({ label: a.name, amount: num(a.balance) }))
    .filter(r => r.amount >= 0.005);

  const assetRows = [
    ...cashBankRows,
    ...(debtors ? [{ label: 'Sundry Debtors', amount: debtors }] : []),
    ...(closingStock ? [{ label: 'Closing Stock', amount: closingStock }] : []),
    ...(tdsRecv ? [{ label: 'TDS Receivable', amount: tdsRecv }] : []),
    ...(gstAsset ? [{ label: 'GST Input Credit', amount: gstAsset }] : []),
    ...otherAssets,
  ];
  const totalAssets = assetRows.reduce((s, r) => s + r.amount, 0);

  // --- Liabilities ---
  const creditors = (state.purchaseBills || [])
    .filter(d => upto(d.date, end))
    .reduce((s, d) => s + Math.max(0, num(d.grandTotal) - num(d.paidAmount)), 0);

  const tdsPay = num(accounts.find(a => a.name === 'TDS Payable')?.balance);

  const excludeLiab = /creditor|payable|tds|gst|output|duties/i;
  const otherLiab = accounts
    .filter(a => !excludeLiab.test(a.name))
    .filter(a => classify(state, a.name) === 'Liability')
    .map(a => ({ label: a.name, amount: -num(a.balance) })) // credit balance shown positive
    .filter(r => r.amount >= 0.005);

  const liabilityRows = [
    ...(creditors ? [{ label: 'Sundry Creditors', amount: creditors }] : []),
    ...(gstLiab ? [{ label: 'Duties & Taxes (GST)', amount: gstLiab }] : []),
    ...(tdsPay ? [{ label: 'TDS Payable', amount: tdsPay }] : []),
    ...otherLiab,
  ];

  // --- Equity ---
  const capitalRows = accounts
    .filter(a => classify(state, a.name) === 'Equity')
    .map(a => ({ label: a.name, amount: -num(a.balance) }))
    .filter(r => Math.abs(r.amount) >= 0.005);
  const netProfit = cumulativeProfit(state, end);
  const equityRows = [
    ...capitalRows,
    { label: netProfit >= 0 ? 'Profit & Loss A/c (Net Profit)' : 'Profit & Loss A/c (Net Loss)', amount: netProfit },
  ];

  const totalLiabAndEquity = [...liabilityRows, ...equityRows].reduce((s, r) => s + r.amount, 0);

  // Reconciling plug for opening balances that don't self-tally.
  const difference = +(totalAssets - totalLiabAndEquity).toFixed(2);

  return {
    assetRows,
    totalAssets,
    liabilityRows,
    equityRows,
    difference,
    // Grand totals shown on each side (plug applied to the deficient side).
    leftTotal: totalLiabAndEquity + (difference > 0 ? difference : 0),
    rightTotal: totalAssets + (difference < 0 ? -difference : 0),
  };
}
